import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';

import { DateTime } from 'luxon';

import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  forkJoin,
  last,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  timer
} from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import { ToastType, NotificationToastService } from '@sondermind/features/notification-toast';
import { TooltipModule } from '@sondermind/features/tooltip';
import {
  ClientPortalReferralsStore,
  ClientReferralMember,
  ClientReferralMembersStore,
  IProvider,
  Provider,
  ProviderStore
} from '@sondermind/data-access/client-portal-referrals';
import { ClientReferralLite } from '@sondermind/data-access/client-referral';
import { Appointment, AppointmentsHttpService } from '@sondermind/data-access/appointments';
import { CreditCard, CreditCardStore } from '@sondermind/data-access/client-credit-card';
import {
  SegmentService,
  IClientSegmentSchema,
  ClientSegmentEvents,
  IDirectBookingStartedData
} from '@sondermind/segment';
import {
  IBenefitsInformation,
  ITempImprovementPatientInsurances,
  PatientInsuranceStore
} from '@sondermind/data-access/client-insurance';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { DirectScheduleSessionHttpService } from '@sondermind/data-access/direct-scheduling';
import { IAppointmentSlot, ProviderCalendarStore } from '@sondermind/data-access/public-facing/provider-calendar';
import { ScheduledEventsHttpService } from '@sondermind/data-access/scheduling';
import { Logger } from '@sondermind/datadog-logging';
import { CoverageNotificationService } from '@sondermind/features/account-edit';
import { ProviderPhotoComponent } from '@sondermind/features/provider-listing/provider-photo';
import {
  AppointmentCarouselComponent,
  IAppointmentSlotSelection
} from '@sondermind/features/public-facing/provider-calendar';
import {
  IQuickCheckoutApiResponse,
  IQuickCheckoutFormValues,
  QuickCheckoutStore,
  SagaProgress,
  TIME_SLOT_UNAVAILABLE_ERROR_CODE,
  TIME_SLOT_UNAVAILABLE_ERROR_MESSAGE
} from '@sondermind/features/public-facing/session-request';
import { IconColors, IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { IrisIconButtonModule, IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { IrisModalRef } from '@sondermind/iris/modal';
import { CommunicationPreference } from '@sondermind/utilities/models-clinical';
import { ISessionRequestPreferences } from '@sondermind/utilities/models-scheduling';
import { SessionPreference, SessionType } from '@sondermind/utilities/models-session';
import { InitialsPipe } from '@sondermind/utilities/pipes';
import { IProviderSearch, } from '@sondermind/data-access/public-facing/provider-directory';
import { LocalizedDatePipe } from '@sondermind/utilities/time-and-date';
import {
  CoverageBadgeComponent,
  CoverageBadgeStore,
  CoverageInfo
} from '@sondermind/features/client-facing/coverage-badge';

import { NativeAppContextHelpers } from '@sondermind/native-app-interface';

import { SessionTaskType, SessionTaskComponent } from '@sondermind/features/client-facing/session-task';

import { FilterOptionsStore } from '@sondermind/data-access/public-facing/filter-form';
import { BookingFlow, BookingStates, DirectBookingData, DirectBookingResult } from './direct-booking-types';

enum SubmissionType {
  DIRECT_SCHEDULE = 'DIRECT_SCHEDULE',
  PROPOSE_TIME = 'PROPOSE_TIME',
  QUICK_CHECKOUT = 'QUICK_CHECKOUT',
  RESCHEDULE = 'RESCHEDULE',
  CANCEL = 'CANCEL'
}
const TOOLTIP_MESSAGES = {
  inNetworkWithEstimate:
    `This is an estimate of your out-of-pocket cost and is not a guarantee of payment. ` +
    `Your final cost may vary based on your specific insurance plan and provider.`,
  inNetworkVerified:
    `We've verified your insurance and confirmed active coverage!\n\n` +
    `After your session, we'll submit your claim and your insurance will determine your final session cost.\n\n` +
    `For specific details about your costs, call the member services number on the back of your insurance card.`,
  insuranceNeedsUpdate:
    `We can't provide a cost estimate until your insurance information is up to date. ` +
    `You can add or update your insurance details on your account after you confirm this session.`,
  cashPay:
    `You will be paying for this session in cash. ` +
    `The full session fee will be charged directly to your payment method.`,
  outOfNetwork:
    `This session is with a provider who is not in your insurance network. ` +
    `You will be responsible for the full session fee, which can be paid directly to the provider.`,
} as const;

@Component({
  selector: 'direct-booking',
  imports: [
    CommonModule,
    ProviderPhotoComponent,
    MatProgressSpinnerModule,
    InitialsPipe,
    IrisIconComponent,
    IrisIconButtonModule,
    AppointmentCarouselComponent,
    LocalizedDatePipe,
    TooltipModule,
    CoverageBadgeComponent,
    SessionTaskComponent
  ],
  templateUrl: './direct-booking.component.html',
  styleUrl: './direct-booking.component.scss'
})
export class DirectBookingComponent implements OnInit, OnDestroy {
  constructor(
    private irisModalRef: IrisModalRef<DirectBookingComponent, DirectBookingData, DirectBookingResult>,
    private providerCalendarStore: ProviderCalendarStore,
    public coverageNotificationService: CoverageNotificationService,
    private currentUser: CurrentUser,
    private creditCardStore: CreditCardStore,
    private router: Router,
    private logger: Logger,
    private quickCheckoutStore: QuickCheckoutStore,
    private directScheduleSessionService: DirectScheduleSessionHttpService,
    private scheduledEventsService: ScheduledEventsHttpService,
    private providerStore: ProviderStore,
    private filterOptionsStore: FilterOptionsStore,
    private notificationToastService: NotificationToastService,
    private clientReferralMembersStore: ClientReferralMembersStore,
    private clientPortalReferralsStore: ClientPortalReferralsStore,
    private segmentService: SegmentService<IClientSegmentSchema>,
    private appointmentsHttpSvc: AppointmentsHttpService,
    private patientInsuranceStore: PatientInsuranceStore,
    private appointmentHttpService: AppointmentsHttpService,
    private coverageBadgeStore: CoverageBadgeStore,
  ) { }

  private readonly statesThatAllowTimeChange: ReadonlySet<BookingStates> = new Set([
    BookingStates.INITIAL,
    BookingStates.INITIAL_WITHOUT_TIMESLOTS,
    BookingStates.RESCHEDULE,
    BookingStates.ERROR
  ]);

  bookingStates = BookingStates;
  irisIconButtonTypes = IrisIconButtonTypes;
  timezone: string = '';
  readonly bookingState = signal<BookingStates>(BookingStates.INITIAL);
  readonly changingTime = signal<boolean>(false);
  readonly appointmentSlots = signal<IAppointmentSlot[]>([]);
  readonly inNetwork = signal<boolean | undefined>(undefined);
  readonly loadingTimeslots = signal<boolean>(false);
  readonly loadingProviderProfile = signal<boolean>(false);
  readonly pendingPATSession = signal<Appointment | undefined>(undefined);
  readonly isSaasInvited = toSignal(this.providerStore.isClientSaasInvited$());

  readonly isInNativeApp = NativeAppContextHelpers.isHostedInNativeApp();

  bookingFlow: BookingFlow = BookingFlow.NEW_CLIENT;
  clientReferralMemberId: number | undefined;
  iconColors = IconColors;
  irisIconSizes = IconSizes;
  timeslot: ISessionRequestPreferences | undefined;
  provider!: IProvider | IProviderSearch;
  appointment: Appointment | undefined;
  cashPay: boolean = false;
  verifiedInsuranceAmount: number | undefined;
  sessionType: string = 'Video';
  certifications: string[] = [];
  benefitsInfo: IBenefitsInformation | undefined;
  readonly expiredCreditCard = signal<CreditCard | undefined>(undefined);
  destroyed$: Subject<void> = new Subject<void>();
  submittingSession: boolean = false;
  errorMessage: string = '';
  providerProfile: Provider | undefined = undefined;
  cachedVerifiedInsuranceNames: string | undefined = undefined;

  readonly sessionTaskType = SessionTaskType;
  readonly userNeedsSetup = toSignal(this.currentUser.user$.pipe(map((user) => user?.needsSetup)));

  readonly verifiedInsuranceNames = signal<string>('');
  readonly unverifiedInsuranceNames = signal<string>('');
  readonly expiredInsuranceNames = signal<string>('');

  readonly hasUnverifiedInsurance = signal<boolean>(false);
  readonly hasInactiveInsurance = signal<boolean>(false);
  readonly hasExpiredInsurance = signal<boolean>(false);
  readonly hasValidInsurance = signal<boolean>(false);

  readonly hasMulitpleIssues = computed(
    () => this.hasUnverifiedInsurance() || this.hasExpiredInsurance() || !!this.expiredCreditCard()
  );

  readonly canChangeTime = computed(
    () => this.statesThatAllowTimeChange.has(this.bookingState()) &&
      !this.changingTime() &&
      this.appointmentSlots().length > 0 &&
      !this.loadingTimeslots()
  );

  readonly canRescheduleInCancelState = computed(() => {
    if (this.bookingState() !== BookingStates.CANCELLING) return false;

    // If there's a pending PAT session, can't reschedule
    if (this.pendingPATSession()) return false;

    // Otherwise check if provider supports rescheduling
    if (!this.provider || !this.isIProvider(this.provider)) return false;
    return this.provider.hasDirectScheduling || this.provider.hasProposeATime;
  });

  readonly showCostEstimate = computed(
    () => this.inNetwork() === true &&
      (this.verifiedInsuranceAmount || this.verifiedInsuranceAmount === 0)
  );

  readonly coverageInfo = signal<CoverageInfo | undefined>(undefined);

  private isIProvider(provider: IProvider | IProviderSearch): provider is IProvider {
    return 'hasDirectScheduling' in provider;
  }

  readonly tooltipText = computed(() => {
    if (this.showCostEstimate()) {
      return TOOLTIP_MESSAGES.inNetworkWithEstimate;
    }
    if (this.inNetwork() === true && !this.verifiedInsuranceAmount && this.verifiedInsuranceAmount !== 0) {
      return TOOLTIP_MESSAGES.inNetworkVerified;
    }
    if (this.hasExpiredInsurance() || this.hasInactiveInsurance() || this.hasUnverifiedInsurance()) {
      return TOOLTIP_MESSAGES.insuranceNeedsUpdate;
    }
    if (this.cashPay) {
      return TOOLTIP_MESSAGES.cashPay;
    }
    // Out of network
    if (this.inNetwork() === false) {
      return TOOLTIP_MESSAGES.outOfNetwork;
    }
    return '';
  });

  /**
   * Only clients who are cash pay clients and don't already have a valid insurance registered
   * will be prompted to add insurance after scheduling.
   * In addition, SAAS-invited clients will always be cash pay, and they can't add insurance on top of that,
   * so don't ever prompt them to add insurance after scheduling.
   * */
  readonly canAddInsurance = computed(() => !this.hasValidInsurance() && this.cashPay && !this.isSaasInvited());

  ngOnInit(): void {
    if (this.irisModalRef.config?.data) {
      this.timeslot = this.irisModalRef.config.data?.slot;
      this.provider = this.irisModalRef.config.data?.providerPreview;
      this.clientReferralMemberId = this.irisModalRef.config.data?.clientReferralMemberId;
      this.bookingFlow = this.irisModalRef.config.data?.bookingFlow;

      this.bookingState.set(this.irisModalRef.config.data?.bookingState ?? BookingStates.INITIAL);
      if (this.bookingState() === BookingStates.RESCHEDULE
        || this.bookingState() === BookingStates.INITIAL_WITHOUT_TIMESLOTS) {
        this.changeTime(true);
      }
      if (this.bookingState() === BookingStates.RESCHEDULE || this.bookingState() === BookingStates.CANCELLING) {
        this.appointment = this.irisModalRef.config.data?.appointment;
        this.timeslot = {
          startTime: this.appointment?.startTime,
          endTime: this.appointment?.endTime,
        };
      }

      if (this.provider) {
        // make sure coverage has been loaded
        // note: this is "no-op" if we have previously loaded info for this provider
        this.coverageBadgeStore.loadCoverageStatusInfo([this.provider.providerId]);

        const coverageInfo = this.coverageBadgeStore.getCoverageInfo(this.provider.providerId);

        // a populated insurance name means "in-network"
        this.inNetwork.set(!!coverageInfo?.insuranceName);
      }

      this.getAppointmentSlots();
      this.checkIfTimeIsAlreadyProposed();
      this.handleCertificationsList();
      this.checkSessionType();
      this.checkCashPay();
      this.loadBenefitsInfo();
      this.checkInsuranceStatus();
      this.checkCreditCardExpired();
      this.loadPersonaProviderProfile();
      this.emitSegmentEvent();
      this.getTimezone();
    }
  }

  checkInsuranceStatus(): void {
    combineLatest([
      this.coverageNotificationService.hasUnverifiedInsurance$,
      this.coverageNotificationService.hasExpiredInsurance$,
      this.coverageNotificationService.hasInactiveInsurance$,
      this.coverageNotificationService.hasValidInsurance$,
      this.coverageNotificationService.validInsurances$,
      this.coverageNotificationService.unverifiedInsurances$,
      this.coverageNotificationService.expiredInsurances$,
    ])
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(([
        hasUnverifiedInsurance, hasExpiredInsurance, hasInactiveInsurance, hasValidInsurance,
        validInsurances, unverifiedInsurances, expiredInsurances
      ]) => {
        this.hasUnverifiedInsurance.set(hasUnverifiedInsurance);
        this.hasExpiredInsurance.set(hasExpiredInsurance);
        this.hasInactiveInsurance.set(hasInactiveInsurance);
        this.hasValidInsurance.set(hasValidInsurance);

        const mapper = (insurances: ITempImprovementPatientInsurances[]) => insurances
          .map((i) => i.providerNetworkCompany?.name)
          .filter((name): name is string => !!name)
          .join(', ') || '';

        this.expiredInsuranceNames.set(mapper(expiredInsurances));
        this.unverifiedInsuranceNames.set(mapper(unverifiedInsurances));

        this.cachedVerifiedInsuranceNames = mapper(validInsurances);
        this.verifiedInsuranceNames.set(this.cachedVerifiedInsuranceNames);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getTimezone(): void {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = { timeZoneName: 'short' };
    const formatter = new Intl.DateTimeFormat('en-US', options); // Or your desired locale
    const formattedDateParts = formatter.formatToParts(now);

    const timeZoneAbbreviation = formattedDateParts.find((part) => part.type === 'timeZoneName')?.value;
    this.timezone = timeZoneAbbreviation ?? '';
  }

  checkIfTimeIsAlreadyProposed(): void {
    if (!this.isIProvider(this.provider)) return;

    this.appointmentHttpService.pendingPATSessionsByProviderId$.pipe(
      take(1)
    ).subscribe((sessions: Record<number, Appointment>) => {
      const session = sessions[this.provider.providerId];

      // Store the pending session in the signal
      this.pendingPATSession.set(session);

      // If not in cancel state and there's a pending session, update booking state
      if (this.bookingState() !== BookingStates.CANCELLING && session) {
        this.bookingState.set(BookingStates.ALREADY_PROPOSED_TIME);
        this.changeTime(false);
      }
    });
  }

  emitSegmentEvent(): void {
    const data: IDirectBookingStartedData = {
      bookingState: this.bookingState().toString(),
      personaClientId: this.currentUser.user?.personaId ?? 0,
      accountComplete: !this.currentUser.user?.needsSetup
    };

    this.segmentService.track<ClientSegmentEvents.DIRECT_BOOKING_STARTED>(
      ClientSegmentEvents.DIRECT_BOOKING_STARTED,
      data
    ).catch(() => {});
  }

  loadPersonaProviderProfile(): void {
    if (!this.provider?.providerId) {
      return;
    }

    if ((this.provider as IProvider)?.hasDirectScheduling === undefined ||
      (this.provider as IProvider)?.hasDirectScheduling === null) {
      this.loadingProviderProfile.set(true);
      this.providerStore.loadProvider$(this.provider.providerAccountId).subscribe((provider) => {
        this.providerProfile = provider;
        this.loadingProviderProfile.set(false);
      });
    } else {
      this.providerProfile = this.provider as Provider;
    }
  }

  directScheduleSession(): void {
    if (!this.timeslot?.startTime || !this.timeslot?.endTime) {
      return;
    }

    const defaultType = this.timeslot?.sessionType === SessionPreference.IN_PERSON ? 'physical' : 'virtual';
    let filteredLocations = this.providerProfile?.locations.filter(
      (loc) => loc.locationType === defaultType && loc.state === this.currentUser.user?.profile?.mailingAddress?.state);

    if (filteredLocations?.length === 0) {
      filteredLocations = this.providerProfile?.locations
        .filter((loc) => loc.state === this.currentUser.user?.profile?.mailingAddress?.state);
    }

    this.directScheduleSessionService.submitDirectScheduleSession({
      locationId: filteredLocations?.[0]?.id ?? 0,
      personaProviderId: this.provider?.providerId ?? 0,
      startTime: DateTime.fromJSDate(this.timeslot?.startTime),
      endTime: DateTime.fromJSDate(this.timeslot?.endTime),
      sessionType: this.timeslot?.sessionType === SessionPreference.IN_PERSON
        ? SessionType.IN_PERSON
        : SessionType.VIDEO,
      timeProposal: !this.providerProfile?.hasDirectScheduling,
      personaClientId: this.currentUser.user?.personaId ?? 0,
      clientReferralMemberId: this.clientReferralMemberId ?? 0
    }).pipe(
      catchError(
        (error: HttpErrorResponse) => this.handleSubmitError(
          error,
          this.providerProfile?.hasDirectScheduling ? SubmissionType.DIRECT_SCHEDULE : SubmissionType.PROPOSE_TIME
        )
      ),
    ).subscribe((session) => {
      this.submittingSession = false;
      if (session) {
        this.refreshStores().subscribe();
        if (this.providerProfile?.hasDirectScheduling) {
          this.bookingState.set(BookingStates.CONFIRMED);
        } else {
          this.bookingState.set(BookingStates.REQUESTED);
        }
      } else {
        this.bookingState.set(BookingStates.ERROR);
        this.errorMessage = 'Sorry! There was an issue booking that time';
      }
    });
  }

  rescheduleSession(): void {
    if (!this.appointment?.id || !this.timeslot?.startTime || !this.timeslot?.endTime) {
      return;
    }

    this.submittingSession = true;
    const sessionType = this.timeslot?.sessionType === SessionPreference.IN_PERSON
      ? SessionType.IN_PERSON
      : SessionType.VIDEO;
    this.directScheduleSessionService.rescheduleSession$(
      this.appointment?.id, this.timeslot?.startTime, this.timeslot?.endTime, sessionType
    ).pipe(
      catchError((error: HttpErrorResponse) => this.handleSubmitError(error, SubmissionType.RESCHEDULE)),
    ).subscribe((session) => {
      this.submittingSession = false;
      if (session) {
        this.refreshStores().subscribe();
        this.bookingState.set(BookingStates.CONFIRMED);
      } else {
        this.bookingState.set(BookingStates.ERROR);
        this.errorMessage = 'Sorry! There was an issue rescheduling that time';
      }
    });
  }

  cancelSession(): void {
    if (!this.appointment?.id || !this.timeslot?.startTime || !this.timeslot?.endTime) {
      return;
    }

    this.submittingSession = true;
    this.scheduledEventsService.cancel$(this.appointment.id, true).pipe(
      switchMap(() => this.refreshStores())
    ).subscribe({
      next: () => {
        this.submittingSession = false;
        this.closeModal(true);
      },
      error: () => {
        this.submittingSession = false;
        this.notificationToastService.showNotificationToast({
          message: 'Sorry! There was an issue canceling that time',
          type: ToastType.ERROR
        });
        this.closeModal(false);
      }
    });
  }

  checkSessionType(): void {
    if (this.timeslot?.sessionType === SessionPreference.IN_PERSON) {
      this.sessionType = 'In person session';
    } else if (this.timeslot?.sessionType === SessionPreference.VIDEO) {
      this.sessionType = 'Video session';
    } else {
      this.sessionType = 'In person or video session';
    }
  }

  checkCreditCardExpired(): void {
    this.creditCardStore.creditCardList.pipe(takeUntil(this.destroyed$)).subscribe((cards) => {
      if (!cards || cards.length === 0) {
        return;
      }
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const expiredCards = cards.filter(
        (card) => card.expYear < currentYear || (card.expYear === currentYear && card.expMonth < currentMonth)
      );
      const validCards = cards.filter(
        (card) => card.expYear > currentYear || (card.expYear === currentYear && card.expMonth > currentMonth)
      );
      if (expiredCards.length > 0 && validCards.length === 0) {
        this.expiredCreditCard.set(expiredCards[0]);
      }
    });
  }

  checkCashPay(): void {
    forkJoin({
      user: this.currentUser.user$.pipe(take(1)),
      pncCashPay: this.coverageNotificationService.hasCashPayInsurance$.pipe(take(1))
    }).subscribe((results) => {
      this.cashPay = results.user?.hasCashPref || results.pncCashPay;
    });
  }

  loadBenefitsInfo(): void {
    this.coverageNotificationService.checkCoverageNotification();

    this.patientInsuranceStore.getBenefitsInfo$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((benefitsInfo) => {
        this.benefitsInfo = benefitsInfo;
        this.checkVerifiedInsuranceAmount();
      });
  }

  checkVerifiedInsuranceAmount(): void {
    this.coverageNotificationService.hasValidInsurance$
      .pipe(take(1))
      .subscribe((hasValidInsurance) => {
        if (hasValidInsurance) {
          this.verifiedInsuranceAmount = this.benefitsInfo?.displayedClientCostEstimate ?? undefined;
          this.emitVerifiedInsuranceAmountSegmentEvent();
        }
      });
  }

  private emitVerifiedInsuranceAmountSegmentEvent(): void {
    this.segmentService.track<ClientSegmentEvents.CLIENT_VIEWED_DIRECT_BOOKING_COST_ESTIMATE>(
      ClientSegmentEvents.CLIENT_VIEWED_DIRECT_BOOKING_COST_ESTIMATE,
      {
        coverageType: this.benefitsInfo?.benefitStructure?.name ?? '',
        payerName: this.cachedVerifiedInsuranceNames ?? '',
        bookingState: this.bookingState().toString()
      }
    ).catch(() => {});
  }

  updateCreditCard(): void {
    this.router.navigate(['/profile/payment']).catch((err) => {
      this.logger.error(err);
    });
    this.closeModal(true);
  }

  updateInsurance(): void {
    this.router.navigate(['/profile/insurance']).catch((err) => {
      this.logger.error(err);
    });
    this.closeModal(true);
  }

  navigateToAccountSetup(): void {
    this.router.navigate(['/profile/account-setup']).catch((err) => {
      this.logger.error(err);
    });
    this.closeModal(true);
  }

  dismissModal(): void {
    const shouldNavigateToDashboard = [
      BookingStates.CONFIRMED,
      BookingStates.REQUESTED,
      BookingStates.ADDED_TO_MATCHES,
      BookingStates.CANCELLING
    ].includes(this.bookingState());

    if (shouldNavigateToDashboard) {
      this.router.navigate(['/dashboard']).catch((err) => {
        this.logger.error(err);
      });
    }
    this.closeModal(shouldNavigateToDashboard);
  }

  handleCertificationsList(): void {
    const providerCertificationAbbrevs = (this.provider?.licensures || [])?.map(
      (cert) => cert.abbreviation).filter((cert) => cert !== ''
    );
    if (providerCertificationAbbrevs?.length > 1) {
      const uniqueCertNames = [...new Set(providerCertificationAbbrevs)];
      this.certifications = uniqueCertNames.length > 1 ? uniqueCertNames?.slice(0, 2) : uniqueCertNames;
      return;
    }

    this.certifications = providerCertificationAbbrevs || [];
  }

  getAppointmentSlots(): void {
    if (!this.provider?.providerId) {
      return;
    }
    this.loadingTimeslots.set(true);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.providerCalendarStore.getAppointmentSlots$(this.provider.providerId, tz).subscribe((slots) => {
      this.appointmentSlots.set(slots);
      this.loadingTimeslots.set(false);
      if (slots?.length === 0 && !this.timeslot?.startTime && !this.timeslot?.endTime) {
        if (this.bookingFlow === BookingFlow.NEW_CLIENT) {
          this.bookingState.set(BookingStates.NEW_CLIENT_NO_TIMESLOTS);
        } else {
          this.bookingState.set(BookingStates.EXISTING_CLIENT_NO_TIMESLOTS);
        }
      }
    });
  }

  handleAppointmentSlotSelectionChanged(newSelection: IAppointmentSlotSelection | null): void {
    if (!newSelection?.startTime) {
      return;
    }
    this.changeTime(false);
    const previousSessionType = this.timeslot?.sessionType;
    const anHourFromStart = new Date(newSelection?.startTime.getTime() + 60 * 60 * 1000);
    this.timeslot = {
      startTime: newSelection?.startTime,
      endTime: newSelection?.endTime ?? anHourFromStart,
      sessionType: previousSessionType,
    } as ISessionRequestPreferences;
  }

  rescheduleCanceledSession(): void {
    this.bookingState.set(BookingStates.RESCHEDULE);
    this.changeTime(true);
  }

  changeTime(value: boolean): void {
    if (value && !this.statesThatAllowTimeChange.has(this.bookingState())) {
      return;
    }
    this.changingTime.set(value);
  }

  closeModal(success: boolean): void {
    const successMessages: Partial<Record<BookingStates, string>> = {
      [BookingStates.CONFIRMED]: 'Your session has been successfully scheduled',
      [BookingStates.REQUESTED]: 'Your session has been requested',
      [BookingStates.ADDED_TO_MATCHES]: 'Provider added to matches successfully',
      [BookingStates.CANCELLING]: 'Your session has been cancelled'
    };

    const bookingResult: DirectBookingResult = {
      bookingState: this.bookingState(),
      message: success ? successMessages[this.bookingState()] || '' : '',
      success
    };

    this.irisModalRef.close(bookingResult);
  }

  submitQuickCheckout(validInsurances: ITempImprovementPatientInsurances[]): void {
    this.errorMessage = '';

    const user = this.currentUser?.user;
    const userProfile = user?.profile;
    const userCommPrefs = userProfile?.communicationPreferences;

    const formValues: IQuickCheckoutFormValues = {
      birthDate: userProfile?.birthDate,
      commPrefs: {
        commPrefsPhone: userCommPrefs?.includes(CommunicationPreference.PHONE) ?? false,
        commPrefsSms: userCommPrefs?.includes(CommunicationPreference.SMS) ?? false,
      },
      contactSmsAllowed: userCommPrefs?.includes(CommunicationPreference.SMS) ?? false,
      email: user?.email,
      firstName: userProfile?.firstName,
      insurance: validInsurances.length > 0 ? validInsurances[0].id : null,
      lastName: userProfile?.lastName,
      location: user?.insuranceState ?? userProfile?.mailingAddress?.state ?? this.filterOptionsStore.getCurrentState() ?? '',
      personaProviderId: this.provider?.providerId ?? 0,
      phone: userProfile?.phoneNumber,
      activeDuty: null,
      heardFrom: {
        source: null,
        details: null
      }
    };
    let sessionRequestPreferences: ISessionRequestPreferences | {} = {};
    if (this.timeslot?.startTime && this.timeslot?.endTime) {
      sessionRequestPreferences = {
        startTime: new Date(this.timeslot?.startTime),
        endTime: new Date(this.timeslot?.endTime),
        sessionType: this.timeslot?.sessionType,
      };
    }

    this.quickCheckoutStore.submitQuickCheckout$(
      formValues,
      sessionRequestPreferences,
    ).pipe(
      catchError((error: HttpErrorResponse) => this.handleSubmitError(error, SubmissionType.QUICK_CHECKOUT)),
      switchMap((resp: IQuickCheckoutApiResponse | null) => this.pollSagaStatus(resp))
    ).subscribe((resp: IQuickCheckoutApiResponse | null) => {
      this.submittingSession = false;
      if (this.bookingState() === BookingStates.NEW_CLIENT_NO_TIMESLOTS && resp?.referral_request_uuid) {
        this.bookingState.set(BookingStates.ADDED_TO_MATCHES);
        this.refreshStores().subscribe();
        this.closeModal(true);
      } else if (resp?.referral_request_uuid) {
        this.bookingState.set(BookingStates.CONFIRMED);
        this.refreshStores().subscribe();
      } else {
        this.bookingState.set(BookingStates.ERROR);
        this.errorMessage = 'Sorry! There was an issue booking that time';
      }
    });
  }

  refreshStores(): Observable<{
    appointments: Appointment[];
    referrals: ClientReferralLite[];
    referralMembers: ClientReferralMember | ClientReferralMember[];
  }> {
    // need to load appointments first, then order doesn't matter
    return this.appointmentsHttpSvc.refresh().pipe(
      switchMap((appointments) => forkJoin({
        appointments: of(appointments),
        referrals: this.clientPortalReferralsStore.refresh(),
        referralMembers: this.clientReferralMemberId
          ? this.clientReferralMembersStore.refreshMember(this.clientReferralMemberId)
          : this.clientReferralMembersStore.getAll$(true)
      }))
    );
  }

  confirmSession(): void {
    if (this.bookingFlow === BookingFlow.EXISTING_CLIENT && (!this.timeslot?.startTime || !this.timeslot?.endTime)) {
      this.notificationToastService.showNotificationToast({
        message: 'Please select a time',
        type: ToastType.ERROR
      });
      return;
    }
    this.submittingSession = true;

    // Use explicit booking flow to determine which booking method to use
    if (this.bookingFlow === BookingFlow.EXISTING_CLIENT) {
      this.directScheduleSession();
      return;
    }

    // Quick checkout flow for new clients
    this.coverageNotificationService.validInsurances$.pipe(take(1)).subscribe((validInsurances) => {
      this.submitQuickCheckout(validInsurances);
    });
  }

  /**
   * Polls the saga status API to check if the quick checkout request has completed
   * @param resp The quick checkout API response containing the saga_id
   * @returns Observable that emits the response if saga completes successfully, or null if it fails/times out
   */
  pollSagaStatus(resp: IQuickCheckoutApiResponse | null): Observable<IQuickCheckoutApiResponse | null> {
    if (!resp) {
      return of(resp);
    }

    const maxPolls = 10;

    return timer(0, 1000).pipe(
      take(maxPolls),
      mergeMap(() => this.quickCheckoutStore.getSagaStatus$(resp.saga_id).pipe(
        map((status) => ({ status, originalResponse: resp }))
      )),
      takeWhile(({ status }) => {
        if (!status) return false;
        return status.progress === SagaProgress.PENDING || status.progress === SagaProgress.IN_PROGRESS;
      }, true),
      // Get the last emitted value (the final status)
      last(),
      // Map back to the appropriate response
      map(({ status, originalResponse }) => {
        if (status?.progress !== SagaProgress.FAILED) {
          return originalResponse;
        }
        // If FAILED, return null to trigger error handling
        return null;
      })
    );
  }

  private handleSubmitError(error: HttpErrorResponse, submissionType: SubmissionType): Observable<null> {
    this.submittingSession = false;
    this.logger.error(`Direct booking submission error: ${JSON.stringify(error)}`, {
      status: error.status,
      bookingState: this.bookingState(),
      bookingFlow: this.bookingFlow,
      providerId: this.provider?.providerId,
      timeslot: this.timeslot,
      submissionType: submissionType.toString(),
    });
    if (error.status === TIME_SLOT_UNAVAILABLE_ERROR_CODE
        && error.message.toLocaleLowerCase().includes(TIME_SLOT_UNAVAILABLE_ERROR_MESSAGE)
    ) {
      // Fetch new appointment slots before showing the calendar
      this.getAppointmentSlots();
      this.changeTime(true);
      this.errorMessage = 'Sorry! There was an issue booking that time';
      this.bookingState.set(BookingStates.ERROR);
    }
    return of(null);
  }
}
